import { useState } from "react";
import { Modal } from "../../common/components/Modal";
import { ModalButtons } from "../../common/components/ModalButtons";
import { Scenario, ScenarioCreatePayload } from "../../../api/scenarios";

interface ScenarioCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: ScenarioCreatePayload) => void;
  existingScenarios: Scenario[];
}

export function ScenarioCreateModal({
  isOpen,
  onClose,
  onSubmit,
  existingScenarios,
}: ScenarioCreateModalProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [cloneFrom, setCloneFrom] = useState<string>("");
  const [capacity, setCapacity] = useState("");
  const [poiLimit, setPoiLimit] = useState("");
  const [batteryFlag, setBatteryFlag] = useState(false);
  const [batteryCapacity, setBatteryCapacity] = useState("");
  const [ppaFlag, setPpaFlag] = useState(false);
  const [ppaCapacity, setPpaCapacity] = useState("");

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    const payload: ScenarioCreatePayload = {
      scenario_name: name.trim(),
    };

    if (description.trim()) payload.description = description.trim();
    if (cloneFrom) payload.clone_from_scenario_id = cloneFrom;
    if (capacity) payload.nameplate_capacity = parseFloat(capacity);
    if (poiLimit) payload.poi_limit = parseFloat(poiLimit);

    if (batteryFlag) {
      payload.battery_flag = true;
      if (batteryCapacity) payload.battery_capacity_mwh = parseFloat(batteryCapacity);
    }
    if (ppaFlag) {
      payload.ppa_flag = true;
      if (ppaCapacity) payload.ppa_capacity = parseFloat(ppaCapacity);
    }

    onSubmit(payload);
    // Reset form
    setName("");
    setDescription("");
    setCloneFrom("");
    setCapacity("");
    setPoiLimit("");
    setBatteryFlag(false);
    setBatteryCapacity("");
    setPpaFlag(false);
    setPpaCapacity("");
  };

  const inputCls =
    "w-full rounded-lg border border-border bg-surface-700 px-3 py-2 text-sm text-text-primary placeholder:text-text-secondary focus:border-quantum-500 focus:outline-none";
  const labelCls = "block text-sm font-medium text-text-secondary mb-1";

  return (
    <Modal title="Create Scenario" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Name */}
        <div>
          <label className={labelCls}>Scenario Name *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Phase 2 - 75MW + Battery"
            className={inputCls}
            required
            autoFocus
          />
        </div>

        {/* Description */}
        <div>
          <label className={labelCls}>Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description of this scenario"
            className={`${inputCls} resize-none`}
            rows={2}
          />
        </div>

        {/* Clone from */}
        {existingScenarios.length > 0 && (
          <div>
            <label className={labelCls}>Clone From</label>
            <select
              value={cloneFrom}
              onChange={(e) => setCloneFrom(e.target.value)}
              className={inputCls}
            >
              <option value="">Start fresh (inherit from project)</option>
              {existingScenarios.map((s) => (
                <option key={s.scenario_id} value={s.scenario_id}>
                  {s.scenario_name}
                  {s.nameplate_capacity ? ` (${s.nameplate_capacity} MW)` : ""}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Override fields */}
        <div className="border-t border-border pt-4">
          <h4 className="text-sm font-semibold text-text-primary mb-3">
            Overrides
            <span className="font-normal text-text-secondary ml-2 text-xs">
              Leave blank to inherit from project
            </span>
          </h4>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Capacity (MW)</label>
              <input
                type="number"
                step="0.01"
                value={capacity}
                onChange={(e) => setCapacity(e.target.value)}
                placeholder="Inherit"
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>POI Limit (MW)</label>
              <input
                type="number"
                step="0.01"
                value={poiLimit}
                onChange={(e) => setPoiLimit(e.target.value)}
                placeholder="Inherit"
                className={inputCls}
              />
            </div>
          </div>
        </div>

        {/* Battery toggle */}
        <div className="space-y-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={batteryFlag}
              onChange={(e) => setBatteryFlag(e.target.checked)}
              className="accent-quantum-500"
            />
            <span className="text-sm text-text-secondary">Include Battery</span>
          </label>
          {batteryFlag && (
            <div>
              <label className={labelCls}>Battery Capacity (MWh)</label>
              <input
                type="number"
                step="0.01"
                value={batteryCapacity}
                onChange={(e) => setBatteryCapacity(e.target.value)}
                placeholder="e.g. 100"
                className={inputCls}
              />
            </div>
          )}
        </div>

        {/* PPA toggle */}
        <div className="space-y-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={ppaFlag}
              onChange={(e) => setPpaFlag(e.target.checked)}
              className="accent-quantum-500"
            />
            <span className="text-sm text-text-secondary">Include PPA</span>
          </label>
          {ppaFlag && (
            <div>
              <label className={labelCls}>PPA Capacity (MW)</label>
              <input
                type="number"
                step="0.01"
                value={ppaCapacity}
                onChange={(e) => setPpaCapacity(e.target.value)}
                placeholder="e.g. 50"
                className={inputCls}
              />
            </div>
          )}
        </div>

        <ModalButtons submitLabel="Create Scenario" onCancel={onClose} />
      </form>
    </Modal>
  );
}
